// SPDX-License-Identifier: MIT
/**
 * Scheme of a shot from the brand palette and the style's background: which colour is the ground,
 * which carries text, which accent stays readable, and how light should combine. Library scenes use
 * it so the same scene works for dark and light brands.
 */
import type { SceneEnv } from '../scene.ts';
import { contrast, mix } from './color.ts';
export type Theme = {
    dark: boolean;
    /** Ground colour. */
    bg: string;
    /** Main text colour. */
    fg: string;
    /** Brand primary as-is (fills, marks, glows). */
    primary: string;
    /** Secondary brand colour. */
    accent: string;
    /** An accent colour that reads as text on `bg` (≥ 3:1), derived from the primary if needed. */
    accentText: string;
    /** Hottest highlight: near white on dark grounds, the primary on light ones. */
    hot: string;
    /** Composite mode for light effects (additive on dark, normal on light). */
    light: GlobalCompositeOperation;
};
/** Mix `color` towards `toward` until it reaches `ratio` contrast against `bg`. */
export function readableTint(color: string, bg: string, toward: string, ratio = 3): string {
    for (let k = 0; k <= 1.0001; k += 0.05) {
        const c = mix(color, toward, k);
        if (contrast(c, bg) >= ratio)
            return c;
    }
    return toward;
}
export function theme(env: Pick<SceneEnv, 'brand' | 'style'>): Theme {
    const { colors } = env.brand;
    const dark = env.style.background === 'dark';
    const bg = dark ? colors.ink : colors.paper;
    const fg = dark ? colors.paper : colors.ink;
    return {
        dark,
        bg,
        fg,
        primary: colors.primary,
        accent: colors.accent,
        accentText: readableTint(colors.primary, bg, fg),
        hot: dark ? mix(colors.paper, '#FFFFFF', 0.6) : colors.primary,
        light: dark ? 'lighter' : 'source-over',
    };
}
